/**
 * Colors for rarity badges, derived from the position a rarity occupies on the
 * scale of its own game (see getRarityInfo in shared/gameMetaData).
 *
 * Nothing is hardcoded per rarity: the stops below are interpolated, so a game
 * with 5 rarities and one with 21 both get a full ramp, and appending a new
 * rarity redistributes the scale on its own.
 *
 * Every stop is dark enough for white text (WCAG AA, worst case 5.02:1 across
 * the whole ramp) so the label color never has to change along the scale. If
 * you retune a stop, keep it below ~48% lightness or the text stops being
 * readable somewhere in the middle of a game's scale.
 */

interface Hsl {
  h: number
  s: number
  l: number
}

const STOPS: Hsl[] = [
  { h: 210, s: 10, l: 40 }, // grey
  { h: 180, s: 55, l: 27 }, // dark teal
  { h: 214, s: 80, l: 42 }, // blue
  { h: 272, s: 62, l: 48 }, // purple
  { h: 322, s: 70, l: 40 }, // magenta
  { h: 390, s: 92, l: 35 }, // amber — 30 + 360, so the hue travels through red instead of back over blue
]

const lerp = (from: number, to: number, t: number): number => from + (to - from) * t

/** position is 0..1, where 1 is the rarest entry of that game */
export const rarityColor = (position: number): string => {
  const scaled = Math.min(Math.max(position, 0), 1) * (STOPS.length - 1)
  const stopIndex = Math.min(Math.floor(scaled), STOPS.length - 2)
  const t = scaled - stopIndex

  const from = STOPS[stopIndex]
  const to = STOPS[stopIndex + 1]

  const h = lerp(from.h, to.h, t)
  const s = lerp(from.s, to.s, t)
  const l = lerp(from.l, to.l, t)

  return `hsl(${h.toFixed(0)}, ${s.toFixed(0)}%, ${l.toFixed(0)}%)`
}
